import React, { useMemo } from 'react';
import {
  Visibility as PreviewIcon,
  ShoppingCart as CartIcon,
} from '@mui/icons-material';
import './VariantCombinationPreview.css';

const VariantCombinationPreview = ({ variants, basePrice }) => {
  // Calculer toutes les combinaisons possibles avec leurs prix
  const combinations = useMemo(() => {
    if (!variants || variants.length === 0) {
      return [];
    }

    // Fonction récursive pour générer toutes les combinaisons
    const generateCombinations = (variantIndex = 0, currentCombination = {}, currentPriceModifier = 0) => {
      if (variantIndex >= variants.length) {
        return [
          {
            ...currentCombination,
            totalPriceModifier: currentPriceModifier,
          },
        ];
      }

      const currentVariant = variants[variantIndex];
      const result = [];

      for (const option of currentVariant.options) {
        const optionPriceModifier =
          currentVariant.influencesPrice && option.priceModifier !== null ? option.priceModifier : 0;

        const newCombination = {
          ...currentCombination,
          [currentVariant.name]: {
            name: option.name,
            priceModifier: optionPriceModifier,
          },
        };

        result.push(
          ...generateCombinations(
            variantIndex + 1,
            newCombination,
            currentPriceModifier + optionPriceModifier
          )
        );
      }

      return result;
    };

    return generateCombinations();
  }, [variants]);

  // Calculer le nombre total de combinaisons
  const totalCombinations = useMemo(
    () => variants.reduce((total, variant) => total * variant.options.length, 1),
    [variants]
  );

  if (!variants || variants.length === 0) {
    return (
      <div className="variant-preview-empty">
        <PreviewIcon className="text-gray-300 text-4xl mb-2" />
        <p className="text-gray-500">Aucune déclinaison définie</p>
        <p className="text-sm text-gray-400">Ajoutez des déclinaisons pour voir les combinaisons possibles</p>
      </div>
    );
  }

  // Afficher seulement les premières combinaisons si il y en a trop
  const displayedCombinations = combinations.slice(0, 12);
  const hasMoreCombinations = combinations.length > 12;

  // Calculer le prix final pour une combinaison
  const calculateFinalPrice = (combination) => {
    const base = parseFloat(basePrice) || 0;
    const modifier = combination.totalPriceModifier || 0;
    return (base + modifier).toFixed(2);
  };

  return (
    <div className="variant-combination-preview">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-medium text-gray-700 flex items-center">
          <PreviewIcon className="mr-2 text-blue-500" />
          Aperçu des combinaisons
        </h4>
        <div className="text-sm text-gray-500">
          {totalCombinations} combinaison{totalCombinations > 1 ? 's' : ''} possible
          {totalCombinations > 1 ? 's' : ''}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {displayedCombinations.map((combination, index) => (
          <div key={index} className="combination-card">
            <div className="combination-header">
              <h5 className="font-medium text-gray-800">Variante {index + 1}</h5>
              {basePrice && (
                <div className="price-display">
                  {combination.totalPriceModifier !== 0 ? (
                    <div className="flex flex-col items-end">
                      <span className="price-tag">{calculateFinalPrice(combination)}€</span>
                      <span className="text-xs text-gray-500">
                        {basePrice}€ {combination.totalPriceModifier >= 0 ? '+' : ''}
                        {combination.totalPriceModifier.toFixed(2)}€
                      </span>
                    </div>
                  ) : (
                    <span className="price-tag">{basePrice}€</span>
                  )}
                </div>
              )}
            </div>
            <div className="combination-details">
              {Object.entries(combination)
                .filter(([key]) => key !== 'totalPriceModifier')
                .map(([variantName, optionData]) => (
                  <div key={variantName} className="variant-detail">
                    <span className="variant-name">{variantName}:</span>
                    <div className="option-display">
                      <span className="option-value">{optionData.name}</span>
                      {optionData.priceModifier !== 0 && (
                        <span className="option-price-modifier">
                          {optionData.priceModifier >= 0 ? '+' : ''}
                          {optionData.priceModifier.toFixed(2)}€
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
            <div className="combination-actions">
              <button className="action-btn preview-btn" title="Aperçu">
                <PreviewIcon fontSize="small" />
              </button>
              <button className="action-btn cart-btn" title="Ajouter au panier">
                <CartIcon fontSize="small" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {hasMoreCombinations && (
        <div className="more-combinations">
          <p className="text-gray-500 text-center">... et {combinations.length - 12} autres combinaisons</p>
        </div>
      )}

      <div className="preview-summary">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="summary-item">
            <h5 className="font-medium text-gray-700">Déclinaisons</h5>
            <p className="text-2xl font-bold text-blue-600">{variants.length}</p>
          </div>
          <div className="summary-item">
            <h5 className="font-medium text-gray-700">Options totales</h5>
            <p className="text-2xl font-bold text-green-600">
              {variants.reduce((total, variant) => total + variant.options.length, 0)}
            </p>
          </div>
          <div className="summary-item">
            <h5 className="font-medium text-gray-700">Combinaisons</h5>
            <p className="text-2xl font-bold text-purple-600">{totalCombinations}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantCombinationPreview;
